import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Construction, MapPin, Users, Calendar, 
  Briefcase, FileText, Plus, Trash2, Upload, Download, Eye,
  Building, User, Clock, CheckCircle2, AlertTriangle, Folder,
  FolderOpen, X, ChevronDown, ChevronRight, FileIcon, AlertCircle,
  ClipboardList, Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import VerbaleFormDialog from '../components/VerbaleFormDialog';
import { offlineSend, newId } from '../lib/offlineQueue';
import PendingForCantiere from '../components/PendingForCantiere';

const API = process.env.REACT_APP_BACKEND_URL;
const QUEUED_MSG = 'Senza rete: salvato sul telefono, verrà inviato appena torna la connessione';
const uploadFile = (url, file, label) => offlineSend({ method: 'post', url, file, label });

const CantiereDetailPage = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cantiere, setCantiere] = useState(null);
  const [ordiniLavoro, setOrdiniLavoro] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [activeTab, setActiveTab] = useState('dati'); // dati, allegati, sopralluoghi, verbali
  const [selectedCartella, setSelectedCartella] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Modal dettaglio sopralluogo
  const [selectedSopralluogo, setSelectedSopralluogo] = useState(null);
  const [showSopralluogoDetail, setShowSopralluogoDetail] = useState(false);
  
  // Modal dettaglio verbale
  const [selectedVerbale, setSelectedVerbale] = useState(null);
  const [showVerbaleDetail, setShowVerbaleDetail] = useState(false);
  
  // Dialog Verbale Form (nuovo)
  const [showVerbaleFormDialog, setShowVerbaleFormDialog] = useState(false);
  const [verbaleFormTipo, setVerbaleFormTipo] = useState('sopralluogo');
  const [sopralluogoPerPdf, setSopralluogoPerPdf] = useState(null); // Dati sopralluogo esistente per generare PDF
  const [sopralluogoSalvato, setSopralluogoSalvato] = useState(false);  // True dopo il salvataggio riuscito
  
  // Sopralluogo form
  const [showSopralluogoForm, setShowSopralluogoForm] = useState(false);
  const [sopralluogoForm, setSopralluogoForm] = useState({
    tipo_attivita: '',
    data_sopralluogo: '',
    durata_ore: '',
    partecipanti: '',
    note: ''
  });
  const sopralluogoFileInputRef = useRef(null);
  const savedVersionRef = useRef(undefined);
  const [uploadingSopralluogoFile, setUploadingSopralluogoFile] = useState(false);
  const [sopralluogoCartella, setSopralluogoCartella] = useState('Verbali di Sopralluogo');
  // PDF generato dal dialog verbale
  const [generatedPdfFile, setGeneratedPdfFile] = useState(null);
  
  // Generazione PDF e upload verbale firmato dal dettaglio sopralluogo
  const [generatingPdfForSopralluogo, setGeneratingPdfForSopralluogo] = useState(false);
  const verbaleFirmatoInputRef = useRef(null);
  const [uploadingVerbaleFirmato, setUploadingVerbaleFirmato] = useState(false);
  
  // Verbale Riunione form
  const [showVerbaleRiunioneForm, setShowVerbaleRiunioneForm] = useState(false);
  const [verbaleRiunioneForm, setVerbaleRiunioneForm] = useState({
    data_riunione: '',
    tipo_verbale: 'Riunione di Coordinamento',
    oggetto: '',
    partecipanti: '',
    note: ''
  });
  const verbaleFileInputRef = useRef(null);
  const [uploadingVerbale, setUploadingVerbale] = useState(false);
  const [selectedVerbaleFile, setSelectedVerbaleFile] = useState(null);
  
  // Tipi di verbale predefiniti
  const tipiVerbale = [
    'Riunione di Coordinamento',
    'Riunione Sicurezza',
    'Riunione Progresso Lavori',
    'Riunione con Committente',
    'Riunione Tecnica',
    'Altro'
  ];
  
  // Progetti collegati
  const [progetti, setProgetti] = useState([]);
  const [progettiCollegati, setProgettiCollegati] = useState([]);
  const [showProgettiSelector, setShowProgettiSelector] = useState(false);
  const [creatingAsBuilt, setCreatingAsBuilt] = useState(false);
  
  const [formData, setFormData] = useState({
    codice_progetto_cliente: '',
    codice_progetto_main_solution: '',
    nome_progetto: '',
    data_inizio: '',
    data_fine: '',
    ordini_lavoro_ids: [],
    progetti_ids: [],
    referente_cantiere_id: '',
    pm_cliente: '',
    potenza_impianto_kwp: '',
    citta: '',
    provincia: '',
    indirizzo: '',
    stato: 'da_iniziare',
    data_inizio_attivita: '',
    data_fine_attivita: '',
    data_inizio_sospensione: '',
    data_fine_sospensione: '',
    data_consegna_as_built: '',
    data_collaudo_prevista: '',
    data_collaudo_finale: '',
    note: '',
    figure_professionali: [],
    allegati: {},
    sopralluoghi: []
  });

  const cartelle = [
    "Nomine", "HSE", "Psc", "Pos Notifica Preliminare", "Verbali",
    "Report fotografici", "Giornali dei Lavori", "Fine Lavori",
    "Materiale trasmesso dal cliente",
    "Verbali di Sopralluogo", "Verbali Riunioni di Coordinamento", "Verbali Direzione dei Lavori"
  ];

  const ruoliAttivita = {
    'assistente_cantiere': 'Assistente di Cantiere',
    'csp': 'CSP (Coordinatore Sicurezza Progettazione)',
    'cse': 'CSE (Coordinatore Sicurezza Esecuzione)',
    'dl': 'DL (Direttore Lavori)',
    'rl': 'RL (Responsabile Lavori)'
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    const onSynced = () => fetchData();
    window.addEventListener('offline-queue-synced', onSynced);
    return () => window.removeEventListener('offline-queue-synced', onSynced);
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Carica il cantiere (obbligatorio)
      const cantiereRes = await axios.get(`${API}/api/cantieri/${id}`, getAuthHeaders());
      setCantiere(cantiereRes.data);
      
      // Carica ordini e utenti in modo non bloccante (potrebbero fallire per permessi)
      try {
        const ordiniRes = await axios.get(`${API}/api/ordini-lavoro`, getAuthHeaders());
        setOrdiniLavoro(ordiniRes.data);
      } catch (ordiniError) {
        console.warn('Ordini non disponibili per questo utente:', ordiniError.response?.data?.detail);
        setOrdiniLavoro([]);
      }
      
      try {
        const utentiRes = await axios.get(`${API}/api/users`, getAuthHeaders());
        setUtenti(utentiRes.data);
      } catch (utentiError) {
        console.warn('Utenti non disponibili per questo utente:', utentiError.response?.data?.detail);
        setUtenti([]);
      }
      
      setFormData({
        codice_progetto_cliente: cantiereRes.data.codice_progetto_cliente || '',
        codice_progetto_main_solution: cantiereRes.data.codice_progetto_main_solution || '',
        nome_progetto: cantiereRes.data.nome_progetto || '',
        data_inizio: cantiereRes.data.data_inizio || '',
        data_fine: cantiereRes.data.data_fine || '',
        ordini_lavoro_ids: cantiereRes.data.ordini_lavoro_ids || [],
        progetti_ids: cantiereRes.data.progetti_ids || [],
        referente_cantiere_id: cantiereRes.data.referente_cantiere_id || '',
        pm_cliente: cantiereRes.data.pm_cliente || '',
        potenza_impianto_kwp: cantiereRes.data.potenza_impianto_kwp || '',
        citta: cantiereRes.data.citta || '',
        provincia: cantiereRes.data.provincia || '',
        indirizzo: cantiereRes.data.indirizzo || '',
        stato: cantiereRes.data.stato || 'da_iniziare',
        data_inizio_attivita: cantiereRes.data.data_inizio_attivita || '',
        data_fine_attivita: cantiereRes.data.data_fine_attivita || '',
        data_inizio_sospensione: cantiereRes.data.data_inizio_sospensione || '',
        data_fine_sospensione: cantiereRes.data.data_fine_sospensione || '',
        data_consegna_as_built: cantiereRes.data.data_consegna_as_built || '',
        data_collaudo_prevista: cantiereRes.data.data_collaudo_prevista || '',
        data_collaudo_finale: cantiereRes.data.data_collaudo_finale || '',
        note: cantiereRes.data.note || '',
        figure_professionali: cantiereRes.data.figure_professionali || [],
        allegati: cantiereRes.data.allegati || {},
        sopralluoghi: cantiereRes.data.sopralluoghi || []
      });
      
      // Carica progetti per trovare quelli collegati
      try {
        const progettiRes = await axios.get(`${API}/api/progetti`, getAuthHeaders());
        setProgetti(progettiRes.data);
        
        // Trova progetti collegati automaticamente (stesso codice cliente o codice MS)
        const codiceCliente = cantiereRes.data.codice_progetto_cliente;
        const codiceMS = cantiereRes.data.codice_progetto_main_solution;
        const progettiIdsManuale = cantiereRes.data.progetti_ids || [];
        
        const collegati = progettiRes.data.filter(p => {
          // Match automatico su codice
          const matchCodice = (codiceCliente && p.codice_progetto_cliente === codiceCliente) ||
                              (codiceMS && p.codice_progetto_main_solution === codiceMS);
          // Match manuale (già selezionato)
          const matchManuale = progettiIdsManuale.includes(p.id);
          return matchCodice || matchManuale;
        });
        
        setProgettiCollegati(collegati);
      } catch (progettiError) {
        console.warn('Progetti non disponibili:', progettiError.response?.data?.detail);
        setProgetti([]);
        setProgettiCollegati([]);
      }
      
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento del cantiere');
      navigate('/cantieri');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per collegare/scollegare un progetto manualmente
  const toggleProgettoCollegato = (progettoId) => {
    const currentIds = formData.progetti_ids || [];
    if (currentIds.includes(progettoId)) {
      setFormData(prev => ({
        ...prev,
        progetti_ids: currentIds.filter(id => id !== progettoId)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        progetti_ids: [...currentIds, progettoId]
      }));
    }
  };

  // Funzione per creare un progetto As Built
  const handleRichiestaAsBuilt = async () => {
    if (!cantiere) return;
    
    setCreatingAsBuilt(true);
    try {
      // Recupera l'utente corrente dal localStorage
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      
      // Crea un nuovo progetto con stato as_built collegato al cantiere
      const nuovoProgetto = {
        nome_progetto: `As Built - ${cantiere.nome_progetto || cantiere.codice_progetto_main_solution}`,
        codice_progetto_cliente: cantiere.codice_progetto_cliente || '',
        codice_progetto_main_solution: cantiere.codice_progetto_main_solution || '',
        tipo_impianto: 'Altro',
        tipologia_progetto: 'As-Built',
        stato: 'as_built',
        data_inizio: new Date().toISOString().split('T')[0],
        ingegnere_responsabile_id: currentUser?.id || '', // Assegna l'utente corrente
        note_tecniche: `Progetto As Built generato dal cantiere: ${cantiere.nome_progetto}\nCodice Cliente: ${cantiere.codice_progetto_cliente}\nCodice MS: ${cantiere.codice_progetto_main_solution}`
      };

      const response = await axios.post(`${API}/api/progetti`, nuovoProgetto, getAuthHeaders());
      
      // Collega il nuovo progetto al cantiere
      const nuovoProgettoId = response.data.id;
      await axios.put(`${API}/api/cantieri/${id}`, {
        progetti_ids: [...(formData.progetti_ids || []), nuovoProgettoId]
      }, getAuthHeaders());
      
      toast.success('Progetto As Built creato e collegato al cantiere!');
      fetchData(); // Ricarica per vedere il nuovo progetto
      
    } catch (error) {
      console.error('Errore creazione As Built:', error);
      toast.error('Errore nella creazione del progetto As Built');
    } finally {
      setCreatingAsBuilt(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const referente = utenti.find(u => u.id === formData.referente_cantiere_id);
      
      const updateData = {
        ...formData,
        referente_cantiere_nome: referente ? `${referente.nome} ${referente.cognome}` : null,
        potenza_impianto_kwp: formData.potenza_impianto_kwp ? parseFloat(formData.potenza_impianto_kwp) : null
      };
      
      await axios.put(`${API}/api/cantieri/${id}`, updateData, getAuthHeaders());
      toast.success('Cantiere aggiornato con successo');
      fetchData();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleOdlChange = (odlId) => {
    if (odlId) {
      const odl = ordiniLavoro.find(o => o.id === odlId);
      setFormData(prev => ({
        ...prev,
        ordini_lavoro_ids: [odlId],
        codice_progetto_cliente: odl?.codice_progetto_cliente || prev.codice_progetto_cliente,
        codice_progetto_main_solution: odl?.codice_progetto_main_solution || prev.codice_progetto_main_solution,
        nome_progetto: odl?.nome_progetto || prev.nome_progetto,
        pm_cliente: odl?.pm_cliente || prev.pm_cliente,
        data_inizio: odl?.data_inizio_lavoro || prev.data_inizio,
        data_fine: odl?.data_fine_lavoro || prev.data_fine
      }));
      toast.success(`Dati aggiornati dall'ordine ${odl?.numero_ordine}`);
    }
  };

  const handleRemoveOdl = () => {
    setFormData(prev => ({ ...prev, ordini_lavoro_ids: [] }));
  };

  const hasFiguraRuolo = (userId, ruolo) => {
    return formData.figure_professionali.some(f => f.user_id === userId && f.ruolo === ruolo);
  };

  const toggleFiguraProfessionale = (userId, ruolo, nomeManuale = null) => {
    let nomeCompleto;
    
    if (nomeManuale) {
      // Figura manuale
      nomeCompleto = nomeManuale;
    } else {
      // Utente dal sistema
      const utente = utenti.find(u => u.id === userId);
      if (!utente) return;
      nomeCompleto = `${utente.nome} ${utente.cognome}`;
    }
    
    const exists = hasFiguraRuolo(userId, ruolo);

    if (exists) {
      setFormData(prev => ({
        ...prev,
        figure_professionali: prev.figure_professionali.filter(f => !(f.user_id === userId && f.ruolo === ruolo))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        figure_professionali: [...prev.figure_professionali, { user_id: userId, nome_completo: nomeCompleto, ruolo, isManuale: !!nomeManuale }]
      }));
    }
  };

  // Gestione Allegati
  const handleFileUpload = async (e) => {
    if (!selectedCartella || !e.target.files?.length) return;
    
    setUploading(true);
    const file = e.target.files[0];
    
    try {
      const res = await uploadFile(
        `${API}/api/cantieri/${id}/allegati/${encodeURIComponent(selectedCartella)}`,
        file,
        `Allegato "${file.name}" in ${selectedCartella}`
      );
      if (res.queued) toast.info(QUEUED_MSG);
      else {
        toast.success('File caricato con successo');
        fetchData();
      }
    } catch (error) {
      toast.error('Errore nel caricamento del file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAllegato = async (cartella, allegatoId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    try {
      await axios.delete(`${API}/api/cantieri/${id}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`, getAuthHeaders());
      toast.success('Allegato eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore eliminazione allegato');
    }
  };

  // Download allegato
  const handleDownloadAllegato = async (cartella, allegatoId, filename) => {
    try {
      const response = await axios.get(
        `${API}/api/cantieri/${id}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Crea link per download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'allegato');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (error) {
      console.error('Errore download:', error);
      toast.error('Errore nel download del file');
    }
  };

  // Gestione Sopralluoghi
  const handleAddSopralluogo = async () => {
    if (!sopralluogoForm.tipo_attivita || !sopralluogoForm.data_sopralluogo) {
      toast.error('Compila tipo attività e data');
      return;
    }
    try {
      const dataToSend = {
        id: newId(),
        tipo_attivita: sopralluogoForm.tipo_attivita,
        data_sopralluogo: sopralluogoForm.data_sopralluogo,
        durata_ore: sopralluogoForm.durata_ore ? parseFloat(sopralluogoForm.durata_ore) : null,
        partecipanti: sopralluogoForm.partecipanti || '',
        descrizione: sopralluogoForm.note || ''  // Backend usa 'descrizione'
      };
      const res = await offlineSend({
        method: 'post',
        url: `${API}/api/cantieri/${id}/sopralluoghi`,
        json: dataToSend,
        label: `${dataToSend.tipo_attivita} del ${dataToSend.data_sopralluogo}`
      });
      
      // Se c'è un PDF generato, caricalo come allegato
      if (generatedPdfFile) {
        const resPdf = await uploadFile(
          `${API}/api/cantieri/${id}/allegati/${encodeURIComponent('Verbali di Sopralluogo')}`,
          generatedPdfFile.file,
          `PDF "${generatedPdfFile.name}"`
        );
        if (!res.queued && !resPdf.queued) toast.success('Sopralluogo e PDF verbale salvati!');
        setGeneratedPdfFile(null);
      } else if (!res.queued) {
        toast.success('Sopralluogo aggiunto');
      }
      if (res.queued) toast.info(QUEUED_MSG);
      
      setShowSopralluogoForm(false);
      setSopralluogoForm({ tipo_attivita: '', data_sopralluogo: '', durata_ore: '', partecipanti: '', note: '' });
      fetchData();
    } catch (error) {
      console.error('Errore:', error.response?.data);
      toast.error('Errore aggiunta sopralluogo: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  // Upload allegato direttamente dal form sopralluogo
  const handleUploadSopralluogoFile = async (e) => {
    if (!e.target.files?.length) return;
    
    setUploadingSopralluogoFile(true);
    const file = e.target.files[0];
    
    try {
      const res = await uploadFile(
        `${API}/api/cantieri/${id}/allegati/${encodeURIComponent(sopralluogoCartella)}`,
        file,
        `Allegato "${file.name}" in ${sopralluogoCartella}`
      );
      if (res.queued) toast.info(QUEUED_MSG);
      else {
        toast.success(`File caricato in "${sopralluogoCartella}"`);
        fetchData();
      }
    } catch (error) {
      toast.error('Errore nel caricamento del file');
    } finally {
      setUploadingSopralluogoFile(false);
      if (sopralluogoFileInputRef.current) sopralluogoFileInputRef.current.value = '';
    }
  };

  const handleDeleteSopralluogo = async (sopralluogoId) => {
    if (!window.confirm('Eliminare questo sopralluogo?')) return;
    try {
      await axios.delete(`${API}/api/cantieri/${id}/sopralluoghi/${sopralluogoId}`, getAuthHeaders());
      toast.success('Sopralluogo eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore eliminazione sopralluogo');
    }
  };

  // Genera PDF verbale per un sopralluogo esistente
  const handleGeneratePdfForSopralluogo = async (sopralluogo) => {
    setGeneratingPdfForSopralluogo(true);
    try {
      // Apri il VerbaleFormDialog con i dati del sopralluogo pre-popolati per generare il PDF
      // Per ora usiamo una generazione semplificata
      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = margin;
      
      // Header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      const tipoTitolo = sopralluogo.tipo_attivita === 'Verbale Riunione di Coordinamento' 
        ? 'VERBALE RIUNIONE DI COORDINAMENTO' 
        : 'VERBALE DI SOPRALLUOGO';
      pdf.text(tipoTitolo, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;
      
      // Dati cantiere
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Cantiere: ${formData.nome_progetto || 'N/D'}`, margin, yPos);
      yPos += 6;
      pdf.text(`Codice MS: ${formData.codice_progetto_main_solution || 'N/D'}`, margin, yPos);
      yPos += 6;
      pdf.text(`Data: ${new Date(sopralluogo.data_sopralluogo).toLocaleDateString('it-IT')}`, margin, yPos);
      yPos += 6;
      if (sopralluogo.durata_ore) {
        pdf.text(`Durata: ${sopralluogo.durata_ore} ore`, margin, yPos);
        yPos += 6;
      }
      if (sopralluogo.partecipanti) {
        pdf.text(`Partecipanti: ${sopralluogo.partecipanti}`, margin, yPos);
        yPos += 6;
      }
      yPos += 10;
      
      // Descrizione
      if (sopralluogo.descrizione || sopralluogo.note) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Descrizione:', margin, yPos);
        yPos += 6;
        pdf.setFont('helvetica', 'normal');
        const descText = sopralluogo.descrizione || sopralluogo.note || '';
        const lines = pdf.splitTextToSize(descText, pageWidth - 2 * margin);
        lines.forEach(line => {
          if (yPos > 270) { pdf.addPage(); yPos = margin; }
          pdf.text(line, margin, yPos);
          yPos += 5;
        });
      }
      
      // Spazio firme
      yPos += 20;
      if (yPos > 240) { pdf.addPage(); yPos = margin; }
      pdf.setFont('helvetica', 'bold');
      pdf.text('FIRME:', margin, yPos);
      yPos += 10;
      pdf.setFont('helvetica', 'normal');
      pdf.line(margin, yPos + 15, margin + 60, yPos + 15);
      pdf.text('Direttore dei Lavori', margin, yPos + 20);
      pdf.line(pageWidth - margin - 60, yPos + 15, pageWidth - margin, yPos + 15);
      pdf.text('Per il Committente', pageWidth - margin - 60, yPos + 20);
      
      // Footer
      pdf.setFontSize(8);
      pdf.text(`Creato da: ${sopralluogo.creato_da_nome || 'N/D'}`, margin, 285);
      pdf.text(`Generato il: ${new Date().toLocaleString('it-IT')}`, pageWidth - margin - 50, 285);
      
      // Salva e carica come allegato
      // Estrai numero verbale dalla descrizione se presente
      const descrizione = sopralluogo.descrizione || '';
      const matchNumero = descrizione.match(/Verbale N\.(\d+)/);
      const numeroVerbale = matchNumero ? matchNumero[1] : 'ND';
      const dataFormatted = new Date(sopralluogo.data_sopralluogo).toLocaleDateString('it-IT').replace(/\//g, '-');
      const nomeCantiere = (formData.nome_progetto || 'Cantiere').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const fileName = `${nomeCantiere}_Verbale_${numeroVerbale}_del_${dataFormatted}.pdf`;
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      // Scarica localmente
      pdf.save(fileName);
      
      // Carica come allegato del cantiere
      const cartella = sopralluogo.tipo_attivita === 'Verbale Riunione di Coordinamento' 
        ? 'Verbali Riunioni di Coordinamento' 
        : 'Verbali di Sopralluogo';
      
      const res = await uploadFile(
        `${API}/api/cantieri/${id}/allegati/${encodeURIComponent(cartella)}`,
        pdfFile,
        `PDF "${fileName}"`
      );
      
      if (res.queued) toast.info(QUEUED_MSG);
      else {
        toast.success('PDF generato e salvato negli allegati!');
        fetchData();
      }
    } catch (error) {
      console.error('Errore generazione PDF:', error);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setGeneratingPdfForSopralluogo(false);
    }
  };
  
  // Upload verbale firmato per un sopralluogo
  const handleUploadVerbaleFirmato = async (e, sopralluogo) => {
    if (!e.target.files?.length) return;
    
    setUploadingVerbaleFirmato(true);
    const file = e.target.files[0];
    
    try {
      const cartella = sopralluogo.tipo_attivita === 'Verbale Riunione di Coordinamento' 
        ? 'Verbali Riunioni di Coordinamento' 
        : 'Verbali di Sopralluogo';
      
      const res = await uploadFile(
        `${API}/api/cantieri/${id}/allegati/${encodeURIComponent(cartella)}`,
        file,
        `Verbale firmato "${file.name}"`
      );
      if (res.queued) toast.info(QUEUED_MSG);
      else {
        toast.success('Verbale firmato caricato con successo!');
        fetchData();
      }
    } catch (error) {
      console.error('Errore upload verbale firmato:', error);
      toast.error('Errore nel caricamento del verbale firmato');
    } finally {
      setUploadingVerbaleFirmato(false);
      if (verbaleFirmatoInputRef.current) verbaleFirmatoInputRef.current.value = '';
    }
  };

  // Gestione Verbali Riunione
  const handleAddVerbaleRiunione = async () => {
    if (!verbaleRiunioneForm.data_riunione || !verbaleRiunioneForm.oggetto) {
      toast.error('Compila data e oggetto della riunione');
      return;
    }
    
    // Prima aggiungi una voce nei sopralluoghi con tipo specifico
    try {
      const tipoVerbaleFinale = verbaleRiunioneForm.tipo_verbale;
      
      const verbaleData = {
        id: newId(),
        tipo_attivita: `Verbale ${tipoVerbaleFinale}`,
        data_sopralluogo: verbaleRiunioneForm.data_riunione,
        descrizione: `Tipo: ${tipoVerbaleFinale}\nOggetto: ${verbaleRiunioneForm.oggetto}\nPartecipanti: ${verbaleRiunioneForm.partecipanti}\n\n${verbaleRiunioneForm.note}`,
        partecipanti: verbaleRiunioneForm.partecipanti || ''
      };
      
      const res = await offlineSend({
        method: 'post',
        url: `${API}/api/cantieri/${id}/sopralluoghi`,
        json: verbaleData,
        label: `${verbaleData.tipo_attivita} del ${verbaleData.data_sopralluogo}`
      });
      toast[res.queued ? 'info' : 'success'](res.queued ? QUEUED_MSG : 'Verbale di riunione aggiunto');
      
      // Se c'è un file selezionato, caricalo direttamente per questo verbale
      if (selectedVerbaleFile) {
        setUploadingVerbale(true);
        await uploadVerbaleFileToSopralluogo(verbaleData.id, selectedVerbaleFile);
        setUploadingVerbale(false);
      }
      
      setShowVerbaleRiunioneForm(false);
      setVerbaleRiunioneForm({ data_riunione: '', tipo_verbale: 'Riunione di Coordinamento', oggetto: '', partecipanti: '', note: '' });
      setSelectedVerbaleFile(null);
      if (verbaleFileInputRef.current) verbaleFileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      console.error('Errore aggiunta verbale:', error);
      toast.error('Errore aggiunta verbale: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };
  
  // Carica file allegato direttamente al verbale specifico
  const uploadVerbaleFileToSopralluogo = async (sopralluogoId, file) => {
    try {
      const res = await uploadFile(
        `${API}/api/cantieri/${id}/sopralluoghi/${sopralluogoId}/allegati/Verbali Riunioni di Coordinamento`,
        file,
        `File verbale "${file.name}"`
      );
      if (!res.queued) toast.success('File verbale caricato');
    } catch (error) {
      console.error('Errore upload file verbale:', error);
      toast.error('Errore caricamento file');
    }
  };

  // Upload file verbale - se siamo nel modal, carica al verbale specifico
  const handleUploadVerbaleFile = async (e) => {
    if (!e.target.files?.length) return;
    
    setUploadingVerbale(true);
    const file = e.target.files[0];
    
    try {
      // Se siamo nel modal dettaglio, carica al verbale specifico
      if (selectedVerbale && showVerbaleDetail) {
        const res = await uploadFile(
          `${API}/api/cantieri/${id}/sopralluoghi/${selectedVerbale.id}/allegati/Verbali Riunioni di Coordinamento`,
          file,
          `File verbale "${file.name}"`
        );
        if (res.queued) {
          toast.info(QUEUED_MSG);
          return;
        }
        toast.success('File allegato al verbale');
        await fetchData();
        // Aggiorna selectedVerbale con i nuovi allegati
        const updatedCantiere = await axios.get(`${API}/api/cantieri/${id}`, getAuthHeaders());
        const updatedVerbale = updatedCantiere.data.sopralluoghi?.find(s => s.id === selectedVerbale.id);
        if (updatedVerbale) {
          const noteLines = (updatedVerbale.descrizione || '').split('\n');
          let oggetto = '', partecipanti = '', noteVerbale = '';
          noteLines.forEach(line => {
            if (line.startsWith('Oggetto:')) oggetto = line.replace('Oggetto:', '').trim();
            else if (line.startsWith('Partecipanti:')) partecipanti = line.replace('Partecipanti:', '').trim();
            else if (line.trim()) noteVerbale += line + '\n';
          });
          setSelectedVerbale({...updatedVerbale, oggetto, partecipanti, noteVerbale});
        }
      } else {
        // Altrimenti carica alla cartella generale
        const res = await uploadFile(
          `${API}/api/cantieri/${id}/allegati/${encodeURIComponent('Verbali Riunioni di Coordinamento')}`,
          file,
          `Verbale "${file.name}"`
        );
        if (res.queued) toast.info(QUEUED_MSG);
        else {
          toast.success('Verbale caricato con successo');
          fetchData();
        }
      }
    } catch (error) {
      console.error('Errore upload:', error);
      toast.error('Errore nel caricamento del verbale');
    } finally {
      setUploadingVerbale(false);
      if (verbaleFileInputRef.current) verbaleFileInputRef.current.value = '';
    }
  };
  
  // Download allegato del verbale specifico
  const handleDownloadVerbaleAllegato = async (sopralluogoId, cartella, allegatoId, filename) => {
    try {
      const response = await axios.get(
        `${API}/api/cantieri/${id}/sopralluoghi/${sopralluogoId}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'allegato');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (error) {
      console.error('Errore download:', error);
      toast.error('Errore nel download del file');
    }
  };
  
  // Elimina allegato del verbale specifico
  const handleDeleteVerbaleAllegato = async (sopralluogoId, cartella, allegatoId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    try {
      await axios.delete(
        `${API}/api/cantieri/${id}/sopralluoghi/${sopralluogoId}/allegati/${encodeURIComponent(cartella)}/${allegatoId}`, 
        getAuthHeaders()
      );
      toast.success('Allegato eliminato');
      await fetchData();
      // Aggiorna selectedVerbale
      const updatedCantiere = await axios.get(`${API}/api/cantieri/${id}`, getAuthHeaders());
      const updatedVerbale = updatedCantiere.data.sopralluoghi?.find(s => s.id === selectedVerbale.id);
      if (updatedVerbale) {
        const noteLines = (updatedVerbale.descrizione || '').split('\n');
        let oggetto = '', partecipanti = '', noteVerbale = '';
        noteLines.forEach(line => {
          if (line.startsWith('Oggetto:')) oggetto = line.replace('Oggetto:', '').trim();
          else if (line.startsWith('Partecipanti:')) partecipanti = line.replace('Partecipanti:', '').trim();
          else if (line.trim()) noteVerbale += line + '\n';
        });
        setSelectedVerbale({...updatedVerbale, oggetto, partecipanti, noteVerbale});
      }
    } catch (error) {
      toast.error('Errore eliminazione allegato');
    }
  };

  // Alert AS Built
  const checkAsBuiltAlert = () => {
    if (formData.data_collaudo_prevista && !formData.data_consegna_as_built) {
      const collaudoDate = new Date(formData.data_collaudo_prevista);
      const oggi = new Date();
      const giorniRimanenti = Math.ceil((collaudoDate - oggi) / (1000 * 60 * 60 * 24));
      
      if (giorniRimanenti <= 5) {
        return { type: 'danger', message: `URGENTE: AS Built deve essere consegnato entro ${giorniRimanenti} giorni (5 giorni prima del collaudo)` };
      } else if (giorniRimanenti <= 10) {
        return { type: 'warning', message: `Attenzione: AS Built deve essere consegnato entro il ${new Date(collaudoDate.getTime() - 5*24*60*60*1000).toLocaleDateString('it-IT')} (5 giorni prima del collaudo)` };
      }
    }
    return null;
  };

  const getStatoColor = (stato) => {
    switch (stato) {
      case 'completato': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_lavorazione': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'sospeso': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Helper per verificare se un utente ha un determinato ambito (supporta ambiti multipli)
  const userHasAmbito = (u, ambito) => {
    if (u.ambiti && u.ambiti.length > 0) {
      return u.ambiti.includes(ambito);
    }
    return u.ambito === ambito;
  };

  // Figure professionali: include ingegneri, cantieristi, admin e master
  const figureProfessionali = utenti.filter(u => 
    userHasAmbito(u, 'ingegnere') || 
    userHasAmbito(u, 'cantierista') || 
    u.ruolo === 'admin' || 
    u.ruolo === 'master'
  );
  
  // Stato per aggiungere figura manuale
  const [nuovaFiguraManuale, setNuovaFiguraManuale] = useState('');
  const [figureManualList, setFigureManualList] = useState([]);
  
  // Carica figure manuali dal formData
  useEffect(() => {
    if (formData.figure_manuali && formData.figure_manuali.length > 0) {
      setFigureManualList(formData.figure_manuali);
    }
  }, [formData.figure_manuali]);
  
  const addFiguraManuale = () => {
    if (nuovaFiguraManuale.trim()) {
      const nuovaFigura = {
        id: `manual_${Date.now()}`,
        nome: nuovaFiguraManuale.trim(),
        cognome: '',
        isManuale: true
      };
      setFigureManualList(prev => [...prev, nuovaFigura]);
      setFormData(prev => ({
        ...prev,
        figure_manuali: [...(prev.figure_manuali || []), nuovaFigura]
      }));
      setNuovaFiguraManuale('');
    }
  };
  
  const removeFiguraManuale = (figuraId) => {
    setFigureManualList(prev => prev.filter(f => f.id !== figuraId));
    setFormData(prev => ({
      ...prev,
      figure_manuali: (prev.figure_manuali || []).filter(f => f.id !== figuraId),
      figure_professionali: prev.figure_professionali.filter(f => f.user_id !== figuraId)
    }));
  };
  
  const asBuiltAlert = checkAsBuiltAlert();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-100 flex items-center justify-center">
        <div className="text-center">
          <Construction className="w-12 h-12 text-orange-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-600">Caricamento cantiere...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/cantieri')} className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Torna ai Cantieri
              </Button>
              <div className="h-8 w-px bg-white/30" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <Construction className="w-7 h-7" />
                  {cantiere?.nome_progetto || 'Dettaglio Cantiere'}
                </h1>
                <p className="text-yellow-100 text-sm mt-1">
                  {cantiere?.codice_progetto_main_solution} • {cantiere?.codice_progetto_cliente}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatoColor(formData.stato)}`}>
                {formData.stato === 'da_iniziare' ? 'Da Iniziare' :
                 formData.stato === 'in_lavorazione' ? 'In Lavorazione' :
                 formData.stato === 'completato' ? 'Completato' :
                 formData.stato === 'sospeso' ? 'Sospeso' : formData.stato}
              </span>
              <Button onClick={handleSave} disabled={saving} className="bg-white text-orange-600 hover:bg-orange-50">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Alert AS Built */}
      {asBuiltAlert && (
        <div className={`max-w-7xl mx-auto px-6 mt-4`}>
          <div className={`p-4 rounded-xl flex items-center gap-3 ${asBuiltAlert.type === 'danger' ? 'bg-red-100 border border-red-300 text-red-800' : 'bg-amber-100 border border-amber-300 text-amber-800'}`}>
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <span className="font-medium">{asBuiltAlert.message}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex flex-wrap gap-2">
          {['dati', 'allegati', 'sopralluoghi', 'verbali'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? (tab === 'verbali' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-600 hover:bg-orange-700') : ''}
            >
              {tab === 'dati' && <FileText className="w-4 h-4 mr-2" />}
              {tab === 'allegati' && <Folder className="w-4 h-4 mr-2" />}
              {tab === 'sopralluoghi' && <Eye className="w-4 h-4 mr-2" />}
              {tab === 'verbali' && <FileText className="w-4 h-4 mr-2" />}
              {tab === 'verbali' ? 'Verbali Riunione' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'allegati' && Object.values(formData.allegati).flat().length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{Object.values(formData.allegati).flat().length}</span>
              )}
              {tab === 'sopralluoghi' && formData.sopralluoghi.filter(s => s.tipo_attivita !== 'Verbale Riunione di Coordinamento').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{formData.sopralluoghi.filter(s => s.tipo_attivita !== 'Verbale Riunione di Coordinamento').length}</span>
              )}
              {tab === 'verbali' && formData.sopralluoghi.filter(s => s.tipo_attivita === 'Verbale Riunione di Coordinamento').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{formData.sopralluoghi.filter(s => s.tipo_attivita === 'Verbale Riunione di Coordinamento').length}</span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6">
        <PendingForCantiere cantiereId={id} />
        
        {/* TAB DATI */}
        {activeTab === 'dati' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* OdL Collegato */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 text-green-600" />
                  Ordine di Lavoro Collegato
                </h2>
                {formData.ordini_lavoro_ids.length > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    {(() => {
                      const odlId = formData.ordini_lavoro_ids[0];
                      const odl = ordiniLavoro.find(o => o.id === odlId);
                      return (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-green-800">📋 {odl ? `${odl.numero_ordine} - ${odl.nome_progetto}` : odlId}</p>
                            {odl && <p className="text-sm text-green-600 mt-1">Cliente: {odl.cliente_nome} • PM: {odl.pm_cliente}</p>}
                          </div>
                          <Button variant="ghost" size="sm" onClick={handleRemoveOdl} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4 mr-1" /> Rimuovi
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Select value="" onValueChange={handleOdlChange}>
                      <SelectTrigger><SelectValue placeholder="Seleziona ordine di lavoro..." /></SelectTrigger>
                      <SelectContent>
                        {ordiniLavoro.map((odl) => (
                          <SelectItem key={odl.id} value={odl.id}>{odl.numero_ordine} - {odl.nome_progetto}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">ℹ️ Collegando un OdL, i campi comuni verranno aggiornati automaticamente</p>
                  </div>
                )}
              </div>

              {/* Dati Progetto */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Dati Progetto
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Codice Main Solution *</Label>
                    <Input value={formData.codice_progetto_main_solution} onChange={(e) => setFormData({...formData, codice_progetto_main_solution: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Codice Cliente</Label>
                    <Input value={formData.codice_progetto_cliente} onChange={(e) => setFormData({...formData, codice_progetto_cliente: e.target.value})} className="mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Nome Progetto *</Label>
                    <Input value={formData.nome_progetto} onChange={(e) => setFormData({...formData, nome_progetto: e.target.value})} className="mt-1" />
                  </div>
                </div>

                {/* Sezione Progetti Collegati */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h3 className="text-md font-semibold text-slate-800 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-purple-600" />
                      Progetti Collegati ({progettiCollegati.length})
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowProgettiSelector(!showProgettiSelector)}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Collega Progetto
                    </Button>
                  </div>

                  {/* Selettore progetti (visibile se showProgettiSelector) */}
                  {showProgettiSelector && (
                    <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Label className="text-sm text-purple-700 mb-2 block">Seleziona progetti da collegare:</Label>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {progetti.filter(p => !progettiCollegati.find(pc => pc.id === p.id)).map(progetto => (
                          <div 
                            key={progetto.id}
                            className="flex items-center justify-between p-2 bg-white rounded border hover:border-purple-300 cursor-pointer"
                            onClick={() => toggleProgettoCollegato(progetto.id)}
                          >
                            <div>
                              <p className="text-sm font-medium">{progetto.nome_progetto}</p>
                              <p className="text-xs text-slate-500">
                                {progetto.codice_progetto_main_solution} | {progetto.tipo_impianto}
                              </p>
                            </div>
                            <Checkbox 
                              checked={(formData.progetti_ids || []).includes(progetto.id)}
                              onCheckedChange={() => toggleProgettoCollegato(progetto.id)}
                            />
                          </div>
                        ))}
                        {progetti.filter(p => !progettiCollegati.find(pc => pc.id === p.id)).length === 0 && (
                          <p className="text-sm text-slate-500 text-center py-2">Nessun altro progetto disponibile</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lista progetti collegati */}
                  {progettiCollegati.length > 0 ? (
                    <div className="space-y-2">
                      {progettiCollegati.map(progetto => {
                        const isAutoMatch = 
                          (formData.codice_progetto_cliente && progetto.codice_progetto_cliente === formData.codice_progetto_cliente) ||
                          (formData.codice_progetto_main_solution && progetto.codice_progetto_main_solution === formData.codice_progetto_main_solution);
                        const isManual = (formData.progetti_ids || []).includes(progetto.id);
                        
                        // Determina il motivo del collegamento
                        let motivoCollegamento = '';
                        if (isAutoMatch && formData.codice_progetto_main_solution === progetto.codice_progetto_main_solution) {
                          motivoCollegamento = `Collegato tramite Codice MS: ${progetto.codice_progetto_main_solution}`;
                        } else if (isAutoMatch && formData.codice_progetto_cliente === progetto.codice_progetto_cliente) {
                          motivoCollegamento = `Collegato tramite Codice Cliente: ${progetto.codice_progetto_cliente}`;
                        } else if (isManual) {
                          motivoCollegamento = 'Collegato manualmente';
                        }
                        
                        return (
                          <div 
                            key={progetto.id}
                            className={`p-3 rounded-lg border ${
                              progetto.stato === 'as_built' 
                                ? 'bg-emerald-50 border-emerald-200' 
                                : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  progetto.stato === 'completato' || progetto.stato === 'as_built' ? 'bg-green-500' :
                                  progetto.stato === 'in_corso' ? 'bg-blue-500' :
                                  progetto.stato === 'sospeso' ? 'bg-red-500' : 'bg-slate-400'
                                }`} />
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{progetto.nome_progetto}</p>
                                  <p className="text-xs text-slate-500">
                                    {progetto.codice_progetto_main_solution} | {progetto.tipo_impianto} | 
                                    <span className={`ml-1 font-medium ${
                                      progetto.stato === 'as_built' ? 'text-emerald-600' : 'text-slate-600'
                                    }`}>
                                      {progetto.stato === 'as_built' ? 'AS BUILT' : 
                                       progetto.stato === 'completato' ? 'Completato' :
                                       progetto.stato === 'in_corso' ? 'In Corso' :
                                       progetto.stato || 'N/D'}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/progetti`)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                            {/* Motivo collegamento come scritta chiara */}
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-purple-600 font-medium">
                                📎 {motivoCollegamento}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                      <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Nessun progetto collegato</p>
                      <p className="text-xs text-slate-400">I progetti vengono collegati automaticamente tramite codice cliente/MS</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Date Cantiere */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Date Cantiere
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Stato Cantiere - spostato qui */}
                  <div className="sm:col-span-2">
                    <Label>Stato Cantiere</Label>
                    <Select value={formData.stato} onValueChange={(value) => setFormData({...formData, stato: value})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="da_iniziare">Da Iniziare</SelectItem>
                        <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                        <SelectItem value="sospeso">Sospeso</SelectItem>
                        <SelectItem value="completato">Completato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Data Inizio Progetto</Label>
                    <Input type="date" value={formData.data_inizio} onChange={(e) => setFormData({...formData, data_inizio: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Data Fine Progetto</Label>
                    <Input type="date" value={formData.data_fine} onChange={(e) => setFormData({...formData, data_fine: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Inizio Attività Cantiere</Label>
                    <Input type="date" value={formData.data_inizio_attivita} onChange={(e) => setFormData({...formData, data_inizio_attivita: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Fine Attività Cantiere</Label>
                    <Input type="date" value={formData.data_fine_attivita} onChange={(e) => setFormData({...formData, data_fine_attivita: e.target.value})} className="mt-1" />
                  </div>
                  
                  {/* Date Sospensione - solo se stato = sospeso */}
                  {formData.stato === 'sospeso' && (
                    <>
                      <div>
                        <Label className="text-orange-700">Inizio Sospensione</Label>
                        <Input type="date" value={formData.data_inizio_sospensione} onChange={(e) => setFormData({...formData, data_inizio_sospensione: e.target.value})} className="mt-1 border-orange-300" />
                      </div>
                      <div>
                        <Label className="text-orange-700">Fine Sospensione</Label>
                        <Input type="date" value={formData.data_fine_sospensione} onChange={(e) => setFormData({...formData, data_fine_sospensione: e.target.value})} className="mt-1 border-orange-300" />
                      </div>
                    </>
                  )}
                  
                  <div className="sm:col-span-2 border-t pt-4 mt-2">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-slate-700">Collaudo e Consegna</h3>
                      <Button
                        size="sm"
                        onClick={handleRichiestaAsBuilt}
                        disabled={creatingAsBuilt}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="richiedi-as-built-btn"
                      >
                        {creatingAsBuilt ? (
                          <span className="animate-pulse">Creazione...</span>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Richiedi As Built
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label>Consegna AS Built</Label>
                        <Input type="date" value={formData.data_consegna_as_built} onChange={(e) => setFormData({...formData, data_consegna_as_built: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label>Collaudo Previsto</Label>
                        <Input type="date" value={formData.data_collaudo_prevista} onChange={(e) => setFormData({...formData, data_collaudo_prevista: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label>Collaudo Finale</Label>
                        <Input type="date" value={formData.data_collaudo_finale} onChange={(e) => setFormData({...formData, data_collaudo_finale: e.target.value})} className="mt-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Località */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-amber-600" />
                  Località e Impianto
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Potenza Impianto (kWp)</Label>
                    <Input type="number" step="0.1" value={formData.potenza_impianto_kwp} onChange={(e) => setFormData({...formData, potenza_impianto_kwp: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input value={formData.citta} onChange={(e) => setFormData({...formData, citta: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input value={formData.provincia} onChange={(e) => setFormData({...formData, provincia: e.target.value})} maxLength={2} className="mt-1" />
                  </div>
                  <div>
                    <Label>Indirizzo</Label>
                    <Input value={formData.indirizzo} onChange={(e) => setFormData({...formData, indirizzo: e.target.value})} className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Note
                </h2>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Inserisci note generali sul cantiere..."
                  rows={4}
                  className="w-full"
                />
              </div>

              {/* Figure Professionali */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-purple-600" />
                  Figure Professionali
                </h2>
                
                {/* Form per aggiungere figura manuale */}
                <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-sm font-medium text-purple-700 mb-2 block">Aggiungi Figura Manuale</Label>
                  <div className="flex gap-2">
                    <Input
                      value={nuovaFiguraManuale}
                      onChange={(e) => setNuovaFiguraManuale(e.target.value)}
                      placeholder="Nome e Cognome (es. Mario Rossi)"
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && addFiguraManuale()}
                    />
                    <Button 
                      type="button" 
                      onClick={addFiguraManuale}
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={!nuovaFiguraManuale.trim()}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-600">Nome</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-600 w-20">Tipo</th>
                        {Object.entries(ruoliAttivita).map(([key]) => (
                          <th key={key} className="text-center py-2 px-2 text-xs font-medium text-slate-600">
                            {key === 'assistente_cantiere' ? 'AC' : key.toUpperCase()}
                          </th>
                        ))}
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Utenti dal sistema (ingegneri e cantieristi) */}
                      {figureProfessionali.map((persona) => (
                        <tr key={persona.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 text-sm">{persona.nome} {persona.cognome}</td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              userHasAmbito(persona, 'cantierista') ? 'bg-orange-100 text-orange-700' :
                              userHasAmbito(persona, 'ingegnere') ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {userHasAmbito(persona, 'cantierista') && userHasAmbito(persona, 'ingegnere') ? 'Entrambi' :
                               userHasAmbito(persona, 'cantierista') ? 'Cantierista' :
                               userHasAmbito(persona, 'ingegnere') ? 'Ingegnere' : 
                               persona.ruolo}
                            </span>
                          </td>
                          {Object.keys(ruoliAttivita).map((ruolo) => (
                            <td key={ruolo} className="text-center py-2 px-2">
                              <Checkbox checked={hasFiguraRuolo(persona.id, ruolo)} onCheckedChange={() => toggleFiguraProfessionale(persona.id, ruolo)} />
                            </td>
                          ))}
                          <td></td>
                        </tr>
                      ))}
                      
                      {/* Figure manuali */}
                      {figureManualList.map((figura) => (
                        <tr key={figura.id} className="border-b border-slate-100 hover:bg-purple-50 bg-purple-50/30">
                          <td className="py-2 px-3 text-sm font-medium text-purple-700">{figura.nome}</td>
                          <td className="py-2 px-3">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                              Manuale
                            </span>
                          </td>
                          {Object.keys(ruoliAttivita).map((ruolo) => (
                            <td key={ruolo} className="text-center py-2 px-2">
                              <Checkbox checked={hasFiguraRuolo(figura.id, ruolo)} onCheckedChange={() => toggleFiguraProfessionale(figura.id, ruolo, figura.nome)} />
                            </td>
                          ))}
                          <td className="py-2 px-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFiguraManuale(figura.id)}
                              className="text-red-500 hover:bg-red-50 h-6 w-6 p-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Colonna Destra */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-teal-600" />
                  Referenti
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>PM Cliente</Label>
                    <Input value={formData.pm_cliente} onChange={(e) => setFormData({...formData, pm_cliente: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Referente Main Solution</Label>
                    <Select value={formData.referente_cantiere_id || "none"} onValueChange={(value) => setFormData({...formData, referente_cantiere_id: value === "none" ? "" : value})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {utenti.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Figure Assegnate
                </h2>
                {formData.figure_professionali.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Nessuna figura assegnata</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(ruoliAttivita).map(([ruolo, label]) => {
                      const figure = formData.figure_professionali.filter(f => f.ruolo === ruolo);
                      if (figure.length === 0) return null;
                      return (
                        <div key={ruolo} className="p-2 bg-slate-50 rounded-lg">
                          <p className="text-xs font-medium text-slate-500 mb-1">{ruolo === 'assistente_cantiere' ? 'AC' : ruolo.toUpperCase()}</p>
                          {figure.map((f, idx) => <span key={idx} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-white border rounded text-xs">{f.nome_completo}</span>)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB ALLEGATI */}
        {activeTab === 'allegati' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1 bg-white rounded-xl shadow-md p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Cartelle</h3>
              <div className="space-y-1">
                {cartelle.map(cartella => (
                  <button
                    key={cartella}
                    onClick={() => setSelectedCartella(cartella)}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      selectedCartella === cartella ? 'bg-orange-100 text-orange-800' : 'hover:bg-slate-100'
                    }`}
                  >
                    {selectedCartella === cartella ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                    {cartella}
                    {(formData.allegati[cartella]?.length || 0) > 0 && (
                      <span className="ml-auto px-2 py-0.5 bg-orange-200 text-orange-800 rounded-full text-xs">
                        {formData.allegati[cartella].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-3 bg-white rounded-xl shadow-md p-6">
              {selectedCartella ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h3 className="font-semibold text-slate-900">{selectedCartella}</h3>
                    <div>
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                      <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-orange-600 hover:bg-orange-700">
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Caricamento...' : 'Carica File'}
                      </Button>
                    </div>
                  </div>
                  
                  {(formData.allegati[selectedCartella]?.length || 0) === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessun file in questa cartella</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formData.allegati[selectedCartella].map((allegato, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileIcon className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium">{allegato.filename}</p>
                              <p className="text-xs text-slate-500">{new Date(allegato.uploaded_at).toLocaleDateString('it-IT')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => window.open(`${API}${allegato.filepath}`, '_blank')}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAllegato(selectedCartella, allegato.id)} className="text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Seleziona una cartella per visualizzare i file</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB SOPRALLUOGHI */}
        {activeTab === 'sopralluoghi' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Sopralluoghi
                </h2>
                <Button onClick={() => { setVerbaleFormTipo('sopralluogo'); setShowVerbaleFormDialog(true); }} size="sm" className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Nuovo Sopralluogo
                </Button>
              </div>
            </div>

            {showSopralluogoForm && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="font-medium text-orange-900 mb-3">Nuovo Sopralluogo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo Attività *</Label>
                    <Select value={sopralluogoForm.tipo_attivita} onValueChange={(v) => setSopralluogoForm({...sopralluogoForm, tipo_attivita: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                      <SelectContent>
                        {Object.values(ruoliAttivita).map(tipo => <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data *</Label>
                    <Input type="date" value={sopralluogoForm.data_sopralluogo} onChange={(e) => setSopralluogoForm({...sopralluogoForm, data_sopralluogo: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Durata (ore)</Label>
                    <Input 
                      type="number" 
                      step="0.5"
                      placeholder="Es: 2.5"
                      value={sopralluogoForm.durata_ore} 
                      onChange={(e) => setSopralluogoForm({...sopralluogoForm, durata_ore: e.target.value})} 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label>Partecipanti</Label>
                    <Input 
                      placeholder="Es: Mario Rossi, Luigi Bianchi..."
                      value={sopralluogoForm.partecipanti} 
                      onChange={(e) => setSopralluogoForm({...sopralluogoForm, partecipanti: e.target.value})} 
                      className="mt-1" 
                    />
                  </div>
                </div>
                
                {/* Note/Descrizione */}
                <div className="mt-4">
                  <Label>Note / Descrizione</Label>
                  <textarea 
                    className="w-full mt-1 p-2 border border-slate-300 rounded-md text-sm resize-none"
                    rows={3}
                    placeholder="Inserisci note, osservazioni, descrizione attività svolte..."
                    value={sopralluogoForm.note} 
                    onChange={(e) => setSopralluogoForm({...sopralluogoForm, note: e.target.value})}
                  />
                </div>
                
                {/* Sezione Carica Allegato */}
                <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                  <Label className="text-orange-800 font-medium">📎 Carica Allegato</Label>
                  
                  {/* Mostra il PDF generato se presente */}
                  {generatedPdfFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-green-800">{generatedPdfFile.name}</p>
                          <p className="text-xs text-green-600">PDF Verbale generato - pronto per essere salvato</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setGeneratedPdfFile(null)}
                        className="text-red-500 hover:bg-red-50 h-6 w-6 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-sm text-slate-600">Cartella destinazione</Label>
                      <Select value={sopralluogoCartella} onValueChange={(v) => setSopralluogoCartella(v)}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {cartelle.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">File</Label>
                      <div className="mt-1">
                        <input type="file" ref={sopralluogoFileInputRef} onChange={handleUploadSopralluogoFile} className="hidden" />
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => sopralluogoFileInputRef.current?.click()} 
                          disabled={uploadingSopralluogoFile}
                          className="w-full border-orange-300 hover:bg-orange-100"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingSopralluogoFile ? 'Caricamento...' : 'Seleziona e Carica'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowSopralluogoForm(false)}>Annulla</Button>
                  <Button 
                    onClick={() => {
                      setVerbaleFormTipo('sopralluogo');
                      setShowVerbaleFormDialog(true);
                    }} 
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <ClipboardList className="w-4 h-4 mr-1" />
                    Genera Verbale PDF
                  </Button>
                  <Button onClick={handleAddSopralluogo} className="bg-orange-600 hover:bg-orange-700">Aggiungi Sopralluogo</Button>
                </div>
              </div>
            )}

            {/* Lista sopralluoghi (escludi verbali riunione) */}
            {formData.sopralluoghi.filter(s => s.tipo_attivita !== 'Verbale Riunione di Coordinamento').length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Eye className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nessun sopralluogo registrato</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.sopralluoghi.filter(s => s.tipo_attivita !== 'Verbale Riunione di Coordinamento').map((s, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 cursor-pointer transition-all"
                    onClick={() => { setSelectedSopralluogo(s); setShowSopralluogoDetail(true); }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-orange-600 text-white text-sm font-medium rounded-full">
                            {s.tipo_attivita}
                          </span>
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(s.data_sopralluogo).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                        {s.note && (
                          <p className="text-sm text-slate-600 line-clamp-2">{s.note}</p>
                        )}
                        {s.durata_ore && (
                          <p className="text-xs text-slate-400 mt-1">⏱️ Durata: {s.durata_ore} ore</p>
                        )}
                        {s.partecipanti && (
                          <p className="text-xs text-slate-400 mt-1">👥 {s.partecipanti}</p>
                        )}
                        {/* Creato da */}
                        {s.creato_da_nome && (
                          <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Aperto da: <span className="font-medium">{s.creato_da_nome}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            // Apri il form per modificare il sopralluogo
                            setSopralluogoPerPdf(s);
                            setVerbaleFormTipo('sopralluogo');
                            setShowVerbaleFormDialog(true);
                          }} 
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          data-testid="modifica-sopralluogo-btn"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifica
                        </Button>
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">Clicca per dettagli</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleDeleteSopralluogo(s.id); }} 
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MODAL DETTAGLIO SOPRALLUOGO */}
        {showSopralluogoDetail && selectedSopralluogo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 bg-orange-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2">
                    <Eye className="w-6 h-6" />
                    Dettaglio Sopralluogo
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowSopralluogoDetail(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Info principali */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs text-slate-500 uppercase">Tipo Attività</Label>
                    <p className="font-semibold text-lg text-orange-700 mt-1">{selectedSopralluogo.tipo_attivita}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs text-slate-500 uppercase">Data Sopralluogo</Label>
                    <p className="font-semibold text-lg mt-1">
                      📅 {new Date(selectedSopralluogo.data_sopralluogo).toLocaleDateString('it-IT', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Durata e Partecipanti */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs text-slate-500 uppercase">Durata</Label>
                    <p className="font-medium mt-1">
                      {selectedSopralluogo.durata_ore ? `⏱️ ${selectedSopralluogo.durata_ore} ore` : '- Non specificata'}
                    </p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs text-slate-500 uppercase">Partecipanti</Label>
                    <p className="font-medium mt-1">
                      {selectedSopralluogo.partecipanti ? `👥 ${selectedSopralluogo.partecipanti}` : '- Non specificati'}
                    </p>
                  </div>
                </div>
                
                {/* Note/Descrizione */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Note / Descrizione</Label>
                  <div className="mt-2 p-3 bg-white rounded border border-slate-200 min-h-[80px]">
                    {selectedSopralluogo.note || selectedSopralluogo.descrizione ? (
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedSopralluogo.note || selectedSopralluogo.descrizione}</p>
                    ) : (
                      <p className="text-slate-400 italic">Nessuna nota inserita</p>
                    )}
                  </div>
                </div>
                
                {/* Allegati - Mostra file dalla cartella Verbali di Sopralluogo */}
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs text-orange-700 uppercase font-semibold flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      Allegati Sopralluogo
                    </Label>
                    <div>
                      <input 
                        type="file" 
                        ref={sopralluogoFileInputRef} 
                        onChange={handleUploadSopralluogoFile} 
                        className="hidden" 
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => sopralluogoFileInputRef.current?.click()}
                        disabled={uploadingSopralluogoFile}
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingSopralluogoFile ? 'Caricamento...' : 'Carica File'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* File dalla cartella Verbali di Sopralluogo */}
                  {formData.allegati['Verbali di Sopralluogo'] && formData.allegati['Verbali di Sopralluogo'].length > 0 ? (
                    <div className="space-y-2">
                      {formData.allegati['Verbali di Sopralluogo'].map((file, fidx) => (
                        <div key={fidx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-100 rounded">
                              <FileText className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{file.filename}</p>
                              <p className="text-xs text-slate-400">
                                Caricato il {new Date(file.uploaded_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadAllegato('Verbali di Sopralluogo', file.id, file.filename)}
                              className="text-blue-600 hover:bg-blue-50"
                              title="Scarica"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAllegato('Verbali di Sopralluogo', file.id)}
                              className="text-red-600 hover:bg-red-50"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Nessun allegato presente</p>
                      <p className="text-xs mt-1">Clicca &quot;Carica File&quot; per aggiungerne uno</p>
                    </div>
                  )}
                </div>
                
                {/* Data creazione */}
                <div className="text-xs text-slate-400 text-right">
                  Creato il: {selectedSopralluogo.created_at ? new Date(selectedSopralluogo.created_at).toLocaleString('it-IT') : 'N/D'}
                </div>
              </div>
              
              {/* Sezione Azioni Verbale */}
              <div className="p-4 border-t border-slate-200 bg-blue-50">
                <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Gestione Verbale PDF
                </h4>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => handleGeneratePdfForSopralluogo(selectedSopralluogo)}
                    disabled={generatingPdfForSopralluogo}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {generatingPdfForSopralluogo ? 'Generazione...' : 'Genera PDF Verbale'}
                  </Button>
                  
                  <div className="relative">
                    <input 
                      type="file"
                      ref={verbaleFirmatoInputRef}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleUploadVerbaleFirmato(e, selectedSopralluogo)}
                      className="hidden"
                    />
                    <Button 
                      variant="outline"
                      onClick={() => verbaleFirmatoInputRef.current?.click()}
                      disabled={uploadingVerbaleFirmato}
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingVerbaleFirmato ? 'Caricamento...' : 'Carica Verbale Firmato'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  💡 Genera il PDF, stampalo, fai firmare e poi ricarica il verbale firmato.
                </p>
              </div>
              
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => { handleDeleteSopralluogo(selectedSopralluogo.id); setShowSopralluogoDetail(false); }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
                <Button onClick={() => setShowSopralluogoDetail(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* TAB VERBALI RIUNIONE */}
        {activeTab === 'verbali' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Verbali Riunioni di Coordinamento
              </h2>
              <div className="flex gap-2">
                <Button onClick={() => { setVerbaleFormTipo('riunione'); setShowVerbaleFormDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Verbale
                </Button>
              </div>
            </div>

            {/* Lista Verbali */}
            {formData.sopralluoghi.filter(s => s.tipo_attivita && s.tipo_attivita.startsWith('Verbale ')).length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nessun verbale di riunione registrato</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.sopralluoghi.filter(s => s.tipo_attivita && s.tipo_attivita.startsWith('Verbale ')).map((s, idx) => {
                  // Parse note per estrarre tipo, oggetto, partecipanti e note
                  const noteLines = (s.descrizione || s.note || '').split('\n');
                  let tipoVerbale = s.tipo_attivita.replace('Verbale ', ''), oggetto = '', partecipanti = '', noteVerbale = '';
                  noteLines.forEach(line => {
                    if (line.startsWith('Tipo:')) tipoVerbale = line.replace('Tipo:', '').trim();
                    else if (line.startsWith('Oggetto:')) oggetto = line.replace('Oggetto:', '').trim();
                    else if (line.startsWith('Partecipanti:')) partecipanti = line.replace('Partecipanti:', '').trim();
                    else if (line.trim()) noteVerbale += line + '\n';
                  });
                  
                  return (
                    <div 
                      key={idx} 
                      className="p-4 bg-purple-50 rounded-lg border border-purple-200 hover:border-purple-400 hover:bg-purple-100 cursor-pointer transition-all"
                      onClick={() => { setSelectedVerbale({...s, tipoVerbale, oggetto, partecipanti, noteVerbale}); setShowVerbaleDetail(true); }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-purple-600 text-white text-sm font-medium rounded-full">
                              📅 {new Date(s.data_sopralluogo).toLocaleDateString('it-IT')}
                            </span>
                            <span className="px-2 py-1 bg-purple-200 text-purple-800 text-xs font-medium rounded">
                              {tipoVerbale}
                            </span>
                            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded">Clicca per dettagli</span>
                          </div>
                          {oggetto && (
                            <p className="font-semibold text-purple-900 text-lg">{oggetto}</p>
                          )}
                          {partecipanti && (
                            <p className="text-sm text-purple-700 mt-1">
                              <span className="font-medium">👥 Partecipanti:</span> {partecipanti}
                            </p>
                          )}
                          {noteVerbale.trim() && (
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{noteVerbale.trim()}</p>
                          )}
                          {/* Creato da */}
                          {s.creato_da_nome && (
                            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Aperto da: <span className="font-medium">{s.creato_da_nome}</span>
                            </p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleDeleteSopralluogo(s.id); }} 
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Link per caricare più file */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500">
                Per caricare altri file, vai alla tab <Button variant="link" className="p-0 h-auto text-orange-600" onClick={() => setActiveTab('allegati')}>Allegati</Button> → Verbali Riunioni di Coordinamento
              </p>
            </div>
          </div>
        )}

        {/* MODAL DETTAGLIO VERBALE */}
        {showVerbaleDetail && selectedVerbale && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 bg-purple-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                    <FileText className="w-6 h-6" />
                    Dettaglio Verbale Riunione
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowVerbaleDetail(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Data */}
                <div className="bg-purple-100 p-4 rounded-lg">
                  <Label className="text-xs text-purple-700 uppercase">Data Riunione</Label>
                  <p className="font-semibold text-xl text-purple-900 mt-1">
                    📅 {new Date(selectedVerbale.data_sopralluogo).toLocaleDateString('it-IT', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </p>
                </div>
                
                {/* Oggetto */}
                {/* Tipo Verbale */}
                <div className="bg-purple-100 p-4 rounded-lg">
                  <Label className="text-xs text-purple-700 uppercase">Tipo Verbale</Label>
                  <p className="font-semibold text-lg mt-1 text-purple-900">
                    {selectedVerbale.tipoVerbale || 'Riunione di Coordinamento'}
                  </p>
                </div>
                
                {/* Oggetto */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Oggetto</Label>
                  <p className="font-semibold text-lg mt-1">
                    {selectedVerbale.oggetto || <span className="text-slate-400 italic">Non specificato</span>}
                  </p>
                </div>
                
                {/* Partecipanti */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Partecipanti</Label>
                  <p className="font-medium mt-1">
                    {selectedVerbale.partecipanti ? `👥 ${selectedVerbale.partecipanti}` : <span className="text-slate-400 italic">Non specificati</span>}
                  </p>
                </div>
                
                {/* Note */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Note</Label>
                  <div className="mt-2 p-3 bg-white rounded border border-slate-200 min-h-[80px]">
                    {selectedVerbale.noteVerbale?.trim() ? (
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedVerbale.noteVerbale.trim()}</p>
                    ) : (
                      <p className="text-slate-400 italic">Nessuna nota</p>
                    )}
                  </div>
                </div>
                
                {/* Allegati - File specifici di questo verbale */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs text-purple-700 uppercase font-semibold flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      File Verbale Allegato
                    </Label>
                    <div>
                      <input 
                        type="file" 
                        ref={verbaleFileInputRef} 
                        onChange={handleUploadVerbaleFile} 
                        className="hidden" 
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => verbaleFileInputRef.current?.click()}
                        disabled={uploadingVerbale}
                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingVerbale ? 'Caricamento...' : 'Carica File'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Mostra solo allegati specifici di questo verbale */}
                  {(() => {
                    const verbaleAllegati = selectedVerbale.allegati?.['Verbali Riunioni di Coordinamento'] || [];
                    return verbaleAllegati.length > 0 ? (
                      <div className="space-y-2">
                        {verbaleAllegati.map((file, fidx) => (
                          <div key={fidx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 rounded">
                                <FileText className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800 text-sm">{file.filename}</p>
                                <p className="text-xs text-slate-400">
                                  Caricato il {new Date(file.uploaded_at).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadVerbaleAllegato(selectedVerbale.id, 'Verbali Riunioni di Coordinamento', file.id, file.filename)}
                                className="text-blue-600 hover:bg-blue-50"
                                title="Scarica"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteVerbaleAllegato(selectedVerbale.id, 'Verbali Riunioni di Coordinamento', file.id)}
                                className="text-red-600 hover:bg-red-50"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400">
                        <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">Nessun file allegato a questo verbale</p>
                        <p className="text-xs mt-1">Clicca &quot;Carica File&quot; per aggiungerne uno</p>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Data creazione */}
                <div className="text-xs text-slate-400 text-right">
                  Creato il: {selectedVerbale.created_at ? new Date(selectedVerbale.created_at).toLocaleString('it-IT') : 'N/D'}
                </div>
              </div>
              
              {/* Sezione Azioni Verbale */}
              <div className="p-4 border-t border-slate-200 bg-purple-50">
                <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Gestione Verbale PDF
                </h4>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => handleGeneratePdfForSopralluogo(selectedVerbale)}
                    disabled={generatingPdfForSopralluogo}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {generatingPdfForSopralluogo ? 'Generazione...' : 'Genera PDF Verbale'}
                  </Button>
                  
                  <div className="relative">
                    <input 
                      type="file"
                      ref={verbaleFirmatoInputRef}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleUploadVerbaleFirmato(e, selectedVerbale)}
                      className="hidden"
                    />
                    <Button 
                      variant="outline"
                      onClick={() => verbaleFirmatoInputRef.current?.click()}
                      disabled={uploadingVerbaleFirmato}
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingVerbaleFirmato ? 'Caricamento...' : 'Carica Verbale Firmato'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  💡 Genera il PDF, stampalo, fai firmare e poi ricarica il verbale firmato.
                </p>
              </div>
              
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => { handleDeleteSopralluogo(selectedVerbale.id); setShowVerbaleDetail(false); }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
                <Button onClick={() => setShowVerbaleDetail(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Verbale Form (Genera PDF) */}
        <VerbaleFormDialog 
          open={showVerbaleFormDialog}
          onOpenChange={(open) => {
            setShowVerbaleFormDialog(open);
            // Reset quando si chiude il dialog
            if (!open) {
              savedVersionRef.current = undefined;
              setSopralluogoPerPdf(null);
              setSopralluogoSalvato(false);  // Reset stato salvato
            }
          }}
          cantiere={cantiere}
          cantiereId={id}
          users={utenti}
          tipoVerbale={verbaleFormTipo}
          figureProfessionaliCantiere={formData.figure_professionali}
          codiceProgettoMS={formData.codice_progetto_main_solution}
          codiceProgettoCliente={formData.codice_progetto_cliente}
          sopralluoghiEsistenti={formData.sopralluoghi || []}
          sopralluogoEsistente={sopralluogoPerPdf}
          isPdfEnabled={sopralluogoSalvato || !!sopralluogoPerPdf}
          onSave={async (verbaleData) => {
            // Salva il sopralluogo/verbale nel backend
            const tipoAttivita = verbaleData.tipo === 'sopralluogo' 
              ? 'Sopralluogo' 
              : 'Verbale Riunione di Coordinamento';
            
            const cartella = verbaleData.tipo === 'sopralluogo' 
              ? 'Verbali di Sopralluogo' 
              : 'Verbali Riunioni di Coordinamento';
            
            const dataToSend = {
              tipo_attivita: tipoAttivita,
              data_sopralluogo: verbaleData.data_verbale,
              durata_ore: verbaleData.durata_ore ? parseFloat(verbaleData.durata_ore) : null,
              partecipanti: verbaleData.partecipanti_riepilogo || sopralluogoPerPdf?.partecipanti || '',
              descrizione: `Verbale N.${verbaleData.numero_verbale}\nOra: ${verbaleData.ora_inizio || ''} - ${verbaleData.ora_fine || ''}\nScopi: ${(verbaleData.scopo_riunione || []).join(', ')}\n${verbaleData.argomenti_discussi || ''}`
            };
            
            const label = `${tipoAttivita} N.${verbaleData.numero_verbale} del ${verbaleData.data_verbale}`;
            let res;
            // Se è un sopralluogo esistente, fai UPDATE invece di POST
            if (sopralluogoPerPdf && sopralluogoPerPdf.id) {
              const url = `${API}/api/cantieri/${id}/sopralluoghi/${sopralluogoPerPdf.id}`;
              res = await offlineSend({
                method: 'put',
                url,
                offlineUrl: `${url}?if_unmodified_since=${encodeURIComponent(savedVersionRef.current ?? sopralluogoPerPdf.updated_at ?? '')}`,
                json: dataToSend,
                label: `Modifica ${label}`
              });
              if (!res.queued) {
                toast.success('Sopralluogo modificato! La modifica è stata registrata.');
                savedVersionRef.current = res.data.updated_at;
              }
            } else {
              // Nuovo sopralluogo
              res = await offlineSend({
                method: 'post',
                url: `${API}/api/cantieri/${id}/sopralluoghi`,
                json: { ...dataToSend, id: newId() },
                label
              });
              if (!res.queued) toast.success('Sopralluogo salvato! Ora puoi generare il PDF.');
            }
            if (res.queued) toast.info(QUEUED_MSG);
            
            // Imposta lo stato salvato PRIMA di fetchData
            setSopralluogoSalvato(true);
            
            // fetchData dopo il salvataggio - NON chiudere il dialog
            fetchData();
          }}
          onPdfSaved={(fileName, cartella) => {
            // Ricarica i dati per vedere il nuovo allegato
            fetchData();
            setShowVerbaleFormDialog(false);
            setSopralluogoPerPdf(null);
          }}
          onPdfGenerated={(pdfFile, fileName) => {
            // Salva il PDF generato nello state per mostrarlo nel form
            setGeneratedPdfFile({ file: pdfFile, name: fileName });
          }}
        />
      </div>
    </div>
  );
};

export default CantiereDetailPage;
